import { IStorageLocal } from "namirasoft-core";
import { NamirasoftMap } from "namirasoft-site-map";
import { JsonFormatter, NSDialog, NSID } from "namirasoft-site-react";
import { Component, MouseEvent, ReactNode } from "react";
import { StatusBadge, StatusCount, StatusKind } from "./StatusBadge";
import Styles from "./Task.module.css";

const PREVIEW_MAX_LENGTH = 100;

export type TaskEntity = { type: string; id: string };

export type TaskItem = {
    rid: string;
    name?: string;
    args?: any[];
    entity: TaskEntity;
};

export type TaskRequested = {
    command: string;
    successful: boolean;
};

export type TaskResult = {
    successful: boolean;
    total_size: number;
    excerpt_size: number;
    excerpt: string;
};

export interface TaskProps
{
    sectionKey: string;
    title?: string;
    tasks: TaskItem[];
    taskRequested: Record<string, TaskRequested>;
    taskResult: Record<string, TaskResult>;
    namirasoftMap: NamirasoftMap;
    storage: IStorageLocal;
    jsonFormatter: JsonFormatter;
    showName?: boolean;
    initiallyOpen?: boolean;
    emptyMessage?: string;
}

interface TaskDialog
{
    title: string;
    value: any;
}

interface TaskState
{
    open: boolean;
    jsonOpen: Record<string, boolean>;
    dialog: TaskDialog | null;
}

class Task extends Component<TaskProps, TaskState>
{
    constructor(props: TaskProps)
    {
        super(props);
        this.state = {
            open: props.initiallyOpen ?? true,
            jsonOpen: {},
            dialog: null,
        };
    }

    private toggle(): void
    {
        this.setState(prev => ({ ...prev, open: !prev.open }));
    }

    private toggleJson(key: string, e: MouseEvent): void
    {
        e.stopPropagation();
        this.setState(prev => ({
            ...prev,
            jsonOpen: { ...prev.jsonOpen, [key]: !prev.jsonOpen[key] }
        }));
    }

    private isJsonOpen(key: string): boolean
    {
        return !!this.state.jsonOpen[key];
    }

    private openDialog(title: string, value: any, e: MouseEvent): void
    {
        e.stopPropagation();
        this.setState(prev => ({ ...prev, dialog: { title, value } }));
    }

    private closeDialog(): void
    {
        this.setState(prev => ({ ...prev, dialog: null }));
    }

    private parseMaybeJson(value: string): any
    {
        try { return JSON.parse(value); }
        catch { return value; }
    }

    private truncate(value: string): { text: string; truncated: boolean }
    {
        if (value.length <= PREVIEW_MAX_LENGTH)
            return { text: value, truncated: false };
        return { text: value.slice(0, PREVIEW_MAX_LENGTH) + "…", truncated: true };
    }

    private renderClickablePreview(raw: string, dialogTitle: string, dialogValue: any): ReactNode
    {
        let { text, truncated } = this.truncate(raw);
        return (
            <span
                className={`${Styles.ns_task_code} ${Styles.ns_task_clickable}`}
                onClick={(e) => this.openDialog(dialogTitle, dialogValue, e)}
                title="Click to view details"
            >
                {text}
                {truncated && <span className={Styles.ns_task_more}> (click to view full)</span>}
            </span>
        );
    }

    private renderJsonButton(key: string): ReactNode
    {
        let active = this.isJsonOpen(key);
        let cls = `${Styles.ns_task_json_button} ${active ? Styles.ns_task_json_button_active : ""}`;
        return (
            <button
                type="button"
                className={cls}
                onClick={(e) => this.toggleJson(key, e)}
                title={active ? "Hide JSON" : "Show JSON"}
            >
                {"{ }"}
            </button>
        );
    }

    private renderJsonPanel(key: string, value: any): ReactNode
    {
        if (!this.isJsonOpen(key))
            return null;
        return (
            <div className={Styles.ns_task_json_panel}>
                {this.props.jsonFormatter.format(value, null!, null!, false)}
            </div>
        );
    }

    private renderCaret(open: boolean): ReactNode
    {
        return (
            <svg
                className={`${Styles.ns_task_caret} ${open ? Styles.ns_task_caret_open : ""}`}
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline points="6 4 10 8 6 12" />
            </svg>
        );
    }

    private renderEntity(entity: TaskEntity | null | undefined): ReactNode
    {
        if (!entity)
            return <span className={Styles.ns_task_field_value}>—</span>;
        return (
            <>
                <div>{entity.type ?? "—"}</div>
                <div className={Styles.ns_task_description}>
                    <NSID id={entity.id} map={this.props.namirasoftMap} storage={this.props.storage} />
                </div>
            </>
        );
    }

    override render(): ReactNode
    {
        let { sectionKey, title, tasks, taskRequested, taskResult, showName, emptyMessage, jsonFormatter } = this.props;
        let open = this.state.open;
        let dialog = this.state.dialog;
        let jsonKey = `${sectionKey}_json`;

        let ok = 0, fail = 0, requested = 0, pending = 0;
        for (let t of tasks)
        {
            let req = taskRequested?.[t.rid];
            let res = taskResult?.[t.rid];
            if (res) (res.successful ? ok++ : fail++);
            else if (req) (req.successful ? requested++ : fail++);
            else pending++;
        }

        let jsonValue = tasks.map(t => ({
            task: t,
            requested: taskRequested?.[t.rid] ?? null,
            result: taskResult?.[t.rid] ?? null,
        }));

        let autoShowName = showName ?? tasks.some(t => !!t.name);

        return (
            <div className={`${Styles.ns_task_section} ${!open ? Styles.ns_task_collapsed : ""}`}>
                {dialog && (
                    <NSDialog onClose={() => this.closeDialog()} center_dialog zIndex={1000}>
                        <div className={Styles.ns_task_dialog_title}>{dialog.title}</div>
                        <div className={Styles.ns_task_dialog_body}>
                            {jsonFormatter.format(dialog.value, null!, null!, false)}
                        </div>
                    </NSDialog>
                )}
                <div className={Styles.ns_task_section_header} onClick={() => this.toggle()}>
                    {this.renderCaret(open)}
                    <span className={Styles.ns_task_section_title}>{title ?? "Tasks"}</span>
                    <span className={Styles.ns_task_section_count}>{tasks.length}</span>
                    <span className={Styles.ns_task_section_summary}>
                        <StatusCount count={ok} label="Successful" status="successful" />
                        <StatusCount count={fail} label="Failed" status="failed" />
                        <StatusCount count={requested} label="Requested" status="pending" />
                        <StatusCount count={pending} label="Pending" status="pending" />
                        {this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                {open && (
                    <div className={Styles.ns_task_section_body}>
                        {this.renderJsonPanel(jsonKey, jsonValue)}
                        {tasks.length === 0
                            ? <div className={Styles.ns_task_section_empty}>{emptyMessage ?? "No tasks yet."}</div>
                            : (
                                <div className={Styles.ns_task_table_wrapper}>
                                    <table className={Styles.ns_task_table}>
                                        <thead>
                                            <tr>
                                                {autoShowName && <th className={Styles.ns_task_col_name}>Name</th>}
                                                <th className={Styles.ns_task_col_name}>Entity</th>
                                                <th>Command</th>
                                                <th className={Styles.ns_task_col_status}>Execution</th>
                                                <th>Result</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tasks.map(t =>
                                            {
                                                let req = taskRequested?.[t.rid];
                                                let res = taskResult?.[t.rid];

                                                let status: StatusKind;
                                                let label: string;
                                                if (res)
                                                {
                                                    status = res.successful ? "successful" : "failed";
                                                    label = res.successful ? "Successful" : "Failed";
                                                }
                                                else if (req)
                                                {
                                                    if (req.successful)
                                                    {
                                                        status = "pending";
                                                        label = "Requested";
                                                    }
                                                    else
                                                    {
                                                        status = "failed";
                                                        label = "Request Failed";
                                                    }
                                                }
                                                else
                                                {
                                                    status = "pending";
                                                    label = "Pending";
                                                }

                                                let truncated = res ? res.excerpt_size < res.total_size : false;
                                                let parsedExcerpt: any = undefined;
                                                if (res?.excerpt)
                                                    parsedExcerpt = this.parseMaybeJson(res.excerpt);

                                                let commandDialogTitle = t.name ? `Command — ${t.name}` : "Command";
                                                let resultDialogTitle = t.name ? `Result — ${t.name}` : "Result";

                                                return (
                                                    <tr key={t.rid}>
                                                        {autoShowName && (
                                                            <td>
                                                                <span className={Styles.ns_task_name}>{t.name ?? "—"}</span>
                                                            </td>
                                                        )}
                                                        <td>{this.renderEntity(t.entity)}</td>
                                                        <td className={Styles.ns_task_col_preview}>
                                                            {req?.command
                                                                ? this.renderClickablePreview(req.command, commandDialogTitle, this.parseMaybeJson(req.command))
                                                                : <span className={Styles.ns_task_field_value}>—</span>
                                                            }
                                                        </td>
                                                                                        <td><StatusBadge status={status} label={label} /></td>
                                                        <td className={Styles.ns_task_col_preview}>
                                                            {res && (
                                                                <>
                                                                    {res.excerpt && this.renderClickablePreview(res.excerpt, resultDialogTitle, parsedExcerpt)}
                                                                    <div className={Styles.ns_task_description}>
                                                                        {res.excerpt_size} / {res.total_size} bytes
                                                                        {truncated ? " (truncated)" : ""}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                    </div>
                )}
            </div>
        );
    }
}

export default Task;